/**
 * Lightweight Web Audio Sound-FX-Engine für das Card-Reveal-System.
 * Komplett synthetisch – keine externen Audiofiles, kein Lade-Lag, klein.
 * Browser-Policy: AudioContext erst nach User-Geste starten – wir initialisieren lazy.
 */

let ctx = null;
let masterGain = null;
let enabled = true;

const ensureCtx = () => {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.6;
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
};

export const setSfxEnabled = (v) => { enabled = !!v; };
export const isSfxEnabled = () => enabled;
export const setSfxVolume = (v) => {
  ensureCtx();
  if (masterGain) masterGain.gain.value = Math.max(0, Math.min(1, v));
};

/** Kurzer perkussiver Tick (Reel-Tick) */
export const playTick = (pitch = 1) => {
  if (!enabled) return;
  const ac = ensureCtx();
  if (!ac) return;
  const t = ac.currentTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = "square";
  osc.frequency.value = 1800 * pitch;
  osc.frequency.exponentialRampToValueAtTime(800 * pitch, t + 0.04);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.25, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
  osc.connect(gain).connect(masterGain);
  osc.start(t);
  osc.stop(t + 0.08);
};

/** Tiefer Whoosh – Pack-Öffnen Initial */
export const playWhoosh = () => {
  if (!enabled) return;
  const ac = ensureCtx();
  if (!ac) return;
  const t = ac.currentTime;
  // Noise-Buffer
  const bufferSize = ac.sampleRate * 0.7;
  const buf = ac.createBuffer(1, bufferSize, ac.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  const noise = ac.createBufferSource();
  noise.buffer = buf;
  const filter = ac.createBiquadFilter();
  filter.type = "bandpass";
  filter.Q.value = 1.2;
  filter.frequency.setValueAtTime(1800, t);
  filter.frequency.exponentialRampToValueAtTime(300, t + 0.7);
  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.5, t + 0.08);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.7);
  noise.connect(filter).connect(gain).connect(masterGain);
  noise.start(t);
  noise.stop(t + 0.75);
};

/** Cinematic Boom + Rising Tone – ein Walkout-Reveal */
export const playReveal = (tier = "standard") => {
  if (!enabled) return;
  const ac = ensureCtx();
  if (!ac) return;
  const t = ac.currentTime;

  const isMega = tier === "champion" || tier === "legendary";
  const boomGainPeak = tier === "champion" ? 0.85 : tier === "legendary" ? 0.75 : 0.55;
  const boomLowFreq = tier === "champion" ? 28 : tier === "legendary" ? 32 : 40;

  // 1. Sub-Bass Boom (intensiver bei mega tiers)
  const boom = ac.createOscillator();
  const boomGain = ac.createGain();
  boom.type = "sine";
  boom.frequency.setValueAtTime(140, t);
  boom.frequency.exponentialRampToValueAtTime(boomLowFreq, t + 0.9);
  boomGain.gain.setValueAtTime(0.0001, t);
  boomGain.gain.exponentialRampToValueAtTime(boomGainPeak, t + 0.04);
  boomGain.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
  boom.connect(boomGain).connect(masterGain);
  boom.start(t);
  boom.stop(t + 1.25);

  // Mega-Tier: Doppel-Boom mit Sub-Layer für mehr Tiefe
  if (isMega) {
    const boom2 = ac.createOscillator();
    const boom2Gain = ac.createGain();
    boom2.type = "triangle";
    boom2.frequency.setValueAtTime(70, t + 0.05);
    boom2.frequency.exponentialRampToValueAtTime(35, t + 1.1);
    boom2Gain.gain.setValueAtTime(0.0001, t + 0.05);
    boom2Gain.gain.exponentialRampToValueAtTime(0.4, t + 0.1);
    boom2Gain.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
    boom2.connect(boom2Gain).connect(masterGain);
    boom2.start(t + 0.05);
    boom2.stop(t + 1.45);

    // Noise-Impact für "Punch"
    const bufSize = ac.sampleRate * 0.4;
    const nbuf = ac.createBuffer(1, bufSize, ac.sampleRate);
    const ndata = nbuf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) ndata[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufSize, 2);
    const noise = ac.createBufferSource();
    noise.buffer = nbuf;
    const nfilter = ac.createBiquadFilter();
    nfilter.type = "lowpass";
    nfilter.frequency.value = 350;
    const ngain = ac.createGain();
    ngain.gain.setValueAtTime(0.0001, t);
    ngain.gain.exponentialRampToValueAtTime(0.6, t + 0.02);
    ngain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    noise.connect(nfilter).connect(ngain).connect(masterGain);
    noise.start(t);
    noise.stop(t + 0.45);
  }

  // 2. Rising sweep (kosmetisch)
  const sweep = ac.createOscillator();
  const sweepGain = ac.createGain();
  sweep.type = "sawtooth";
  sweep.frequency.setValueAtTime(220, t);
  sweep.frequency.exponentialRampToValueAtTime(isMega ? 1760 : 1320, t + 0.7);
  sweepGain.gain.setValueAtTime(0.0001, t);
  sweepGain.gain.exponentialRampToValueAtTime(isMega ? 0.22 : 0.15, t + 0.3);
  sweepGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
  const sweepFilter = ac.createBiquadFilter();
  sweepFilter.type = "lowpass";
  sweepFilter.frequency.value = 2400;
  sweep.connect(sweepFilter).connect(sweepGain).connect(masterGain);
  sweep.start(t);
  sweep.stop(t + 0.95);

  // 3. Tier-spezifischer Chime / Fanfare
  if (tier === "champion") {
    // Triumphierende Fanfare: Lila-Dur-Akkord-Arpeggio + Glissando
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1568, 2093]; // C5–C7 Spread
    notes.forEach((freq, i) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = i % 2 === 0 ? "triangle" : "sine";
      o.frequency.value = freq;
      const start = t + 0.18 + i * 0.07;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.22, start + 0.025);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.7);
      o.connect(g).connect(masterGain);
      o.start(start);
      o.stop(start + 0.75);
    });
    // Sustained Pad-Akkord für Atmosphäre
    [261.63, 329.63, 392, 523.25].forEach((freq) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t + 0.4);
      g.gain.exponentialRampToValueAtTime(0.08, t + 0.7);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
      o.connect(g).connect(masterGain);
      o.start(t + 0.4);
      o.stop(t + 1.85);
    });
  } else if (tier === "legendary") {
    // Heroischer Aufstieg
    const notes = [659.25, 880, 1108.73, 1318.51, 1760, 2349.32];
    notes.forEach((freq, i) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = "triangle";
      o.frequency.value = freq;
      const start = t + 0.22 + i * 0.075;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.21, start + 0.022);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.62);
      o.connect(g).connect(masterGain);
      o.start(start);
      o.stop(start + 0.7);
    });
  } else if (tier === "walkout") {
    const notes = [659.25, 880, 1108.73];
    notes.forEach((freq, i) => {
      const o = ac.createOscillator();
      const g = ac.createGain();
      o.type = "triangle";
      o.frequency.value = freq;
      const start = t + 0.25 + i * 0.08;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.55);
      o.connect(g).connect(masterGain);
      o.start(start);
      o.stop(start + 0.6);
    });
  }
};

/** Stage-Reveal-Tick: kleiner aufsteigender Triangle-Ping für jede enthüllte Sektion (Legendary-Walkout) */
export const playStageReveal = (stage = 1) => {
  if (!enabled) return;
  const ac = ensureCtx();
  if (!ac) return;
  const t = ac.currentTime;
  // Aufsteigende Frequenzen pro Stage
  const freqs = [0, 523.25, 659.25, 783.99, 987.77, 1318.51]; // C5, E5, G5, B5, E6
  const f = freqs[Math.max(1, Math.min(5, stage))] || 659.25;
  // Whoosh / Reveal-Swoosh: Noise-Burst + Triangle-Ping
  const bufSize = ac.sampleRate * 0.18;
  const nbuf = ac.createBuffer(1, bufSize, ac.sampleRate);
  const ndata = nbuf.getChannelData(0);
  for (let i = 0; i < bufSize; i++) ndata[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufSize, 1.5);
  const noise = ac.createBufferSource();
  noise.buffer = nbuf;
  const nfilter = ac.createBiquadFilter();
  nfilter.type = "bandpass";
  nfilter.Q.value = 2;
  nfilter.frequency.value = 1400 + stage * 280;
  const ngain = ac.createGain();
  ngain.gain.setValueAtTime(0.0001, t);
  ngain.gain.exponentialRampToValueAtTime(0.18, t + 0.02);
  ngain.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
  noise.connect(nfilter).connect(ngain).connect(masterGain);
  noise.start(t);
  noise.stop(t + 0.2);
  // Ping
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = "triangle";
  o.frequency.value = f;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.22, t + 0.025);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
  o.connect(g).connect(masterGain);
  o.start(t);
  o.stop(t + 0.5);
  // Stage 5 (Foto+Name) = extra Boom für Big-Finish
  if (stage === 5) {
    const boom = ac.createOscillator();
    const bg = ac.createGain();
    boom.type = "sine";
    boom.frequency.setValueAtTime(110, t);
    boom.frequency.exponentialRampToValueAtTime(45, t + 0.6);
    bg.gain.setValueAtTime(0.0001, t);
    bg.gain.exponentialRampToValueAtTime(0.5, t + 0.04);
    bg.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
    boom.connect(bg).connect(masterGain);
    boom.start(t);
    boom.stop(t + 0.85);
  }
};

/** Sanfter „Pop" beim Schließen / Tier-Indikator */
export const playPop = () => {
  if (!enabled) return;
  const ac = ensureCtx();
  if (!ac) return;
  const t = ac.currentTime;
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = "sine";
  o.frequency.setValueAtTime(600, t);
  o.frequency.exponentialRampToValueAtTime(1200, t + 0.1);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.3, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
  o.connect(g).connect(masterGain);
  o.start(t);
  o.stop(t + 0.2);
};
